// Carga de Vencimientos (PP-23): por cada planta de la cuenta, sus documentos
// (con los tipos que exige) y su nomina, con concurrencia acotada, y despues
// la logica pura junta todo.
//
// Deuda anotada en el PR: son tres lecturas por planta desde el navegador.
// Con una lectura multiplanta de documentos y nomina en la API (ADR-025 /
// PP-63 cubre viajes, ofertas y carga, no documentos) esto pasa a una sola llamada
// y collectExpiries no cambia.
//
// Una planta que falla no tapa a las demas: se muestra lo que se pudo leer y
// el nombre de la que falto. El error no entra al estado, solo su tipo.
import { useCallback, useEffect, useRef, useState } from "react";
import type { PortalSession } from "../auth/session.js";
import type { DocumentsPort } from "../fleet/documents/port.js";
import { FleetError, type FleetPort } from "../fleet/port.js";
import { collectExpiries, mapWithConcurrency, type Expiries, type SiteExpiryInput } from "../logic/expiries.js";
import type { SiteAccess } from "../sites/port.js";

/** Plantas leidas a la vez: suficiente para una cuenta con varias plantas sin saturar la API. */
export const EXPIRIES_CONCURRENCY = 3;

export type ExpiriesPort = Pick<FleetPort, "listVehicles" | "listDrivers"> & DocumentsPort;

export type ExpiriesState =
  | { estado: "cargando" }
  | { estado: "denegado" }
  | { estado: "error" }
  | { estado: "listo"; expiries: Expiries; failedSites: readonly string[] };

export interface ExpiriesHandle {
  state: ExpiriesState;
  /** Relee todo sin pasar por "cargando" (despues de una renovacion). */
  refresh: () => void;
  reload: () => void;
}

export function useExpiries(
  port: ExpiriesPort,
  session: PortalSession,
  sites: readonly SiteAccess[],
  concurrency: number = EXPIRIES_CONCURRENCY,
): ExpiriesHandle {
  const [state, setState] = useState<ExpiriesState>({ estado: "cargando" });
  const [round, setRound] = useState(0);
  const silentNext = useRef(false);
  // Clave estable de las plantas: el shell puede pasar un arreglo nuevo en
  // cada render y eso no tiene que disparar otra lectura.
  const sitesKey = JSON.stringify(sites.map((s) => ({ siteId: s.siteId, name: s.name, providerId: s.providerId })));

  useEffect(() => {
    let current = true;
    const silent = silentNext.current;
    silentNext.current = false;
    if (!silent) setState({ estado: "cargando" });
    const targets = JSON.parse(sitesKey) as SiteAccess[];
    void mapWithConcurrency(targets, concurrency, async (site): Promise<SiteExpiryInput> => {
      const fleetSite = { siteId: site.siteId, providerId: site.providerId };
      const [docs, vehicles, drivers] = await Promise.all([
        port.listDocuments(session, fleetSite),
        port.listVehicles(session, fleetSite),
        port.listDrivers(session, fleetSite),
      ]);
      return {
        site: { siteId: site.siteId, name: site.name, providerId: site.providerId },
        today: docs.today,
        documents: docs.documents,
        requiredDocuments: docs.requiredDocuments,
        vehicles: vehicles.items,
        drivers: drivers.items,
      };
    }).then((results) => {
      if (!current) return;
      const inputs: SiteExpiryInput[] = [];
      const failedSites: string[] = [];
      results.forEach((result, index) => {
        if (result.ok) inputs.push(result.value);
        else failedSites.push(targets[index]!.name);
      });
      if (targets.length > 0 && inputs.length === 0) {
        const denied = results.every((r) => !r.ok && r.error instanceof FleetError && r.error.kind === "access");
        setState({ estado: denied ? "denegado" : "error" });
        return;
      }
      setState({ estado: "listo", expiries: collectExpiries(inputs), failedSites });
    });
    return () => {
      current = false;
    };
  }, [port, session, sitesKey, concurrency, round]);

  const refresh = useCallback(() => {
    silentNext.current = true;
    setRound((n) => n + 1);
  }, []);
  const reload = useCallback(() => {
    setRound((n) => n + 1);
  }, []);

  return { state, refresh, reload };
}
